"use client"
import {
    getDefaultValuesFromFeatures,
    getFeaturesByProductType
} from "../../../services/features/features"
// import { fetchProductType } from "@/source/utils/api-service/get-methods"
import { fetchProductType } from "../../../utils/api-service/frontend"
import { postMethod } from "../../../utils/api-service/api-service"
import React, { useEffect, useState, useCallback, useRef } from "react"
import BasicInput from "../ConfiguratorInputs/BasicInput/BasicInput"
import styles from "./Configurator.module.css"
import { inputPreparers } from "../../../helpers/DummyRunnerHelper/EngineInputPreprocessor"
import { SpecialInputs } from "../ConfiguratorInputs/Specialnputs/SpecialInputs"
import {
    Accordion,
    AccordionBody,
    AccordionHeader,
    AccordionItem
} from "react-bootstrap"
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io"

const Configurator = ({
    id,
    onPriceCalculated,
    getUserInputs,
    onCalculating,
    onError,
    existingUserInputs,
    existingUserSelections,
    priceType
}) => {
    const [location, setLocation] = useState({ country: "IN" })
    const [isLocationLoaded, setIsLocationLoaded] = useState(false)
    const [productId, setProductId] = useState("")
    const [productType, setProductType] = useState("CURTAIN")
    const [parentProductType, setParentProductType] = useState("CURTAIN")
    const [featureGroups, setFeatureGroups] = useState([])
    const [values, setValues] = useState({})
    const [isInitialized, setIsInitialized] = useState(false)
    const [isEditMode, setIsEditMode] = useState(false)
    const [isCalculatingInitial, setIsCalculatingInitial] = useState(false)

    // Track if we've applied existing selections
    const hasAppliedExistingSelections = useRef(false)
    const hasCalculatedInitial = useRef(false)

    useEffect(() => {
        console.log("Values: ", values)
    }, [values])

    const getProductTypeAndFeatures = async () => {
        try {
            const response = await fetchProductType(id)
            if (response.success) {
                setProductType(response.productType)
                setParentProductType(response?.parentProductType)
                const groups = getFeaturesByProductType(response.productType)
                setFeatureGroups(groups)

                // Check if we have existing selections (edit mode)
                const hasExistingSelections =
                    existingUserSelections &&
                    Object.keys(existingUserSelections).length > 0

                if (hasExistingSelections) {
                    setIsEditMode(true)
                    // Merge existing selections with default values
                    const defaultValues = getDefaultValuesFromFeatures(groups)
                    const mergedValues = { ...defaultValues, ...existingUserSelections }

                    // Handle nested structures (like hardware)
                    if (existingUserSelections.hardware) {
                        mergedValues.hardware = {
                            ...defaultValues.hardware,
                            ...existingUserSelections.hardware
                        }
                    }

                    setValues(mergedValues)
                    setIsInitialized(true)
                    hasAppliedExistingSelections.current = true
                } else {
                    // New product mode - use default values
                    const defaultValues = getDefaultValuesFromFeatures(groups)
                    setValues(defaultValues)
                    setIsInitialized(true)
                }
            }
        } catch (error) {
            console.log("Error fetching product type:", error)
            onError?.("Failed to load product configuration")
        }
    }

    useEffect(() => {
        if (id) {
            setProductId(id)
            getProductTypeAndFeatures()
        }
    }, [id])

    // Calculate initial price when location is loaded and configurator is initialized
    useEffect(() => {
        if (!isInitialized) return
        if (hasCalculatedInitial.current) return

        if (Object.keys(values).length > 0) {
            hasCalculatedInitial.current = true
            // Small delay to ensure everything is ready
            setTimeout(() => {
                calculatePrice(values)
            }, 100)
        }
    }, [isInitialized, values])

    // Helper to check if hardware selection is complete
    const isHardwareComplete = useCallback(inputs => {
        if (!inputs.hardware) return true
        if (inputs.hardware.hardwareType === "NONE") return true

        const { hardwareType, motorizedType, vendor } = inputs.hardware

        if (hardwareType === "MANUAL") {
            return !!(hardwareType && vendor && vendor.trim() !== "")
        }

        if (hardwareType === "MOTORIZED") {
            return !!(hardwareType && motorizedType && vendor && vendor.trim() !== "")
        }

        return false
    }, [])

    // Helper to check if dimensions are valid
    const areDimensionsValid = useCallback(
        inputs => {
            if (
                ["CURTAIN", "ROMAN_BLIND", "ROLLER_BLIND", "WOODEN_BLIND"].includes(
                    parentProductType
                )
            ) {
                return !!(inputs.dimensions?.width && inputs.dimensions?.height)
            }
            return true
        },
        [parentProductType]
    )

    // Calculate price function
    const calculatePrice = useCallback(
        async inputs => {
            if (!productId) return

            onCalculating?.(true)

            try {
                if (!areDimensionsValid(inputs)) {
                    onCalculating?.(false)
                    return
                }

                if (
                    ["CURTAIN", "ROMAN_BLIND", "ROLLER_BLIND", "WOODEN_BLIND"].includes(
                        parentProductType
                    )
                ) {
                    if (!isHardwareComplete(inputs)) {
                        onCalculating?.(false)
                        return
                    }
                }

                const preparer = inputPreparers[parentProductType]
                const calculatorInput = preparer({ ...inputs })

                const response = await postMethod(
                    `/products/calculate/${productId}`,
                    {
                        productType: parentProductType,
                        priceType: priceType,
                        ...calculatorInput
                    }
                )

                if (response.success) {
                    getUserInputs?.(response.result?.total, calculatorInput, {
                        ...inputs
                    })
                    onPriceCalculated?.(response.result?.breakdown?.total?.formatted, response?.result?.breakdown?.components);
                } else {
                    console.error("Calculation failed:", response.message)
                    onError?.(response.message || "Calculation failed")
                }
            } catch (error) {
                console.error("Error calculating price:", error)
                onError?.("Error calculating price")
            } finally {
                onCalculating?.(false)
            }
        },
        [
            productId,
            parentProductType,
            onPriceCalculated,
            onCalculating,
            onError,
            getUserInputs,
            areDimensionsValid,
            isHardwareComplete,
            priceType
        ]
    )

    // Recalculate price when location changes (if needed)
    useEffect(() => {
        if (
            isInitialized &&
            values &&
            Object.keys(values).length > 0
        ) {
            // Only recalculate if we already have values and location was just loaded
            if (hasCalculatedInitial.current) {
                calculatePrice(values)
            }
        }
    }, [priceType])

    // Flatten all features for dependency checking
    const getAllFeatures = useCallback(() => {
        const features = []
        featureGroups.forEach(group => {
            features.push(...group.features)
        })
        return features
    }, [featureGroups])

    const getValueByPath = useCallback((path, valuesObj) => {
        if (!path) return undefined

        if (path.includes(".")) {
            const parts = path.split(".")
            let current = valuesObj
            for (const part of parts) {
                if (current && typeof current === "object") {
                    current = current[part]
                } else {
                    return undefined
                }
            }
            return current
        }

        return valuesObj[path]
    }, [])

    const setFeatureOptions = useCallback((path, options) => {
        setFeatureGroups(prevGroups => {
            const newGroups = [...prevGroups]
            for (const group of newGroups) {
                for (const feature of group.features) {
                    if (feature.type === "nested" && feature.features) {
                        const nestedFeature = feature.features.find(f => f.path === path)
                        if (nestedFeature) {
                            nestedFeature.options = options
                            return newGroups
                        }
                    } else if (feature.path === path || feature.slug === path) {
                        feature.options = options
                        return newGroups
                    }
                }
            }
            return newGroups
        })
    }, [])

    const findFeatureByPath = useCallback(
        path => {
            for (const group of featureGroups) {
                for (const feature of group.features) {
                    if (feature.type === "nested" && feature.features) {
                        const nestedFeature = feature.features.find(f => f.path === path)
                        if (nestedFeature) return nestedFeature
                    } else if (feature.path === path || feature.slug === path) {
                        return feature
                    }
                }
            }
            return null
        },
        [featureGroups]
    )

    const isFeatureVisible = useCallback(
        (feature, parentPath) => {
            if (!feature.dependencies || feature.dependencies.length === 0) {
                return true
            }

            return feature.dependencies.some(dep => {
                const depPath = dep.featureSlug
                if (!depPath) return false

                const dependentValue = getValueByPath(depPath, values)
                const conditionValue = dep.condition

                return dependentValue === conditionValue
            })
        },
        [values, getValueByPath]
    )

    const getVisibleFeatures = useCallback(
        (features, parentPath) => {
            return features
                .filter(feature => {
                    const isVisible = isFeatureVisible(feature, parentPath)
                    return isVisible
                })
                .map(feature => {
                    if (feature.type === "nested" && feature.features) {
                        const newParentPath = parentPath
                            ? `${parentPath}.${feature.slug}`
                            : feature.slug

                        return {
                            ...feature,
                            features: getVisibleFeatures(feature.features, newParentPath)
                        }
                    }
                    return feature
                })
        },
        [isFeatureVisible]
    )

    // Filter options based on dependencies
    const getFilteredOptions = useCallback(
        feature => {
            if (
                !feature.dependencies ||
                feature.dependencies.length === 0 ||
                !feature.options
            ) {
                return feature.options
            }

            const matchedDependency = feature.dependencies.find(dep => {
                const dependentValue = values[dep.featureSlug]
                return dependentValue === dep.condition
            })

            if (!matchedDependency || !matchedDependency.allowedValues) {
                return feature.options
            }

            return feature.options.filter(opt =>
                matchedDependency.allowedValues.includes(opt.value)
            )
        },
        [values]
    )

    const batchUpdatesRef = useRef(new Map())
    const batchTimeoutRef = useRef(null)

    // Single update function
    const applySingleUpdate = useCallback(
        (slugOrPath, value) => {
            setValues(prev => {
                let newValues

                if (slugOrPath.includes(".")) {
                    const [parent, child] = slugOrPath.split(".")
                    const currentParent = prev[parent] || {}
                    newValues = {
                        ...prev,
                        [parent]: {
                            ...currentParent,
                            [child]: value
                        }
                    }
                } else {
                    newValues = {
                        ...prev,
                        [slugOrPath]: value
                    }
                }

                const allFeatures = getAllFeatures()
                const dependentFeatures = allFeatures.filter(f =>
                    f.dependencies?.some(dep => {
                        const depPath = dep.featureSlug
                        return (
                            depPath === slugOrPath || depPath === slugOrPath.split(".")[0]
                        )
                    })
                )

                dependentFeatures.forEach(f => {
                    if (newValues[f.slug] !== undefined) {
                        delete newValues[f.slug]
                    }
                })

                return newValues
            })
        },
        [getAllFeatures]
    )

    // Batch update function
    const applyBatchUpdates = useCallback(
        updates => {
            setValues(prev => {
                let currentValues = prev

                updates.forEach(({ path, value }) => {
                    if (path.includes(".")) {
                        const [parent, child] = path.split(".")
                        const currentParent = currentValues[parent] || {}
                        currentValues = {
                            ...currentValues,
                            [parent]: {
                                ...currentParent,
                                [child]: value
                            }
                        }
                    } else {
                        currentValues = {
                            ...currentValues,
                            [path]: value
                        }
                    }
                })

                const allFeatures = getAllFeatures()
                updates.forEach(({ path }) => {
                    const dependentFeatures = allFeatures.filter(f =>
                        f.dependencies?.some(dep => {
                            const depPath = dep.featureSlug
                            return depPath === path || depPath === path.split(".")[0]
                        })
                    )

                    dependentFeatures.forEach(f => {
                        if (currentValues[f.slug] !== undefined) {
                            delete currentValues[f.slug]
                        }
                    })
                })

                return currentValues
            })
        },
        [getAllFeatures]
    )

    // Main handleFeatureChange with batch support
    const handleFeatureChange = useCallback(
        async (slugOrPath, value, isBatch = false) => {
            console.log("Handle Feature changed called: ", slugOrPath, value, isBatch)
            const feature = findFeatureByPath(slugOrPath)

            if (isBatch) {
                console.log("Is in batch:", isBatch)
                batchUpdatesRef.current.set(slugOrPath, value)

                if (batchTimeoutRef.current) {
                    clearTimeout(batchTimeoutRef.current)
                }

                batchTimeoutRef.current = setTimeout(() => {
                    const updates = Array.from(batchUpdatesRef.current.entries()).map(
                        ([path, val]) => ({
                            path,
                            value: val
                        })
                    )

                    console.log("Applying batch updates:", updates)
                    applyBatchUpdates(updates)
                    batchUpdatesRef.current.clear()
                    batchTimeoutRef.current = null
                }, 0)

                return { feature, newVals: null, isBatched: true }
            } else {
                console.log("Is not in batch:", isBatch)
                let newVals

                await Promise.resolve(
                    setValues(prev => {
                        let newValues

                        if (slugOrPath.includes(".")) {
                            const [parent, child] = slugOrPath.split(".")
                            const currentParent = prev[parent] || {}
                            newValues = {
                                ...prev,
                                [parent]: {
                                    ...currentParent,
                                    [child]: value
                                }
                            }
                        } else {
                            newValues = {
                                ...prev,
                                [slugOrPath]: value
                            }
                        }

                        const allFeatures = getAllFeatures()
                        const dependentFeatures = allFeatures.filter(f =>
                            f.dependencies?.some(dep => {
                                const depPath = dep.featureSlug
                                return (
                                    depPath === slugOrPath || depPath === slugOrPath.split(".")[0]
                                )
                            })
                        )

                        dependentFeatures.forEach(f => {
                            if (newValues[f.slug] !== undefined) {
                                delete newValues[f.slug]
                            }
                        })

                        newVals = newValues
                        return newValues
                    })
                )

                return { feature, newVals, isBatched: false }
            }
        },
        [findFeatureByPath, getAllFeatures, applyBatchUpdates]
    )

    // Updated helpers for onFeatureChange
    const handleOnChange = async (slugOrPath, value) => {
        const { feature, newVals, isBatched } = await handleFeatureChange(
            slugOrPath,
            value
        )

        if (feature?.onFeatureChange) {
            const helpers = {
                setValue: async (path, val) => {
                    await handleFeatureChange(path, val, true)
                },
                setValues: async updates => {
                    applyBatchUpdates(updates)
                },
                setOptions: setFeatureOptions,
                handleFeatureChange: handleFeatureChange,
                flushBatch: () => {
                    if (batchTimeoutRef.current) {
                        clearTimeout(batchTimeoutRef.current)
                        const updates = Array.from(batchUpdatesRef.current.entries()).map(
                            ([path, val]) => ({
                                path,
                                value: val
                            })
                        )
                        applyBatchUpdates(updates)
                        batchUpdatesRef.current.clear()
                        batchTimeoutRef.current = null
                    }
                }
            }

            const currentValues = {
                ...values,
                [slugOrPath]: value,
                productType: productType,
                parentProductType: parentProductType,
            }

            console.log("currentValue: ", currentValues)

            try {
                await feature.onFeatureChange(value, currentValues, helpers)

                if (batchUpdatesRef.current.size > 0) {
                    helpers.flushBatch()
                }

                //@ts-ignore
                if (!isBatched && newVals && feature?.affectsPrice) {
                    await calculatePrice(newVals)
                } else if (isBatched) {
                    setTimeout(async () => {
                        setValues(currentVals => {
                            if (feature.affectsPrice) {
                                calculatePrice(currentVals)
                            }
                            return currentVals
                        })
                    }, 10)
                }
            } catch (error) {
                console.error("Error in onFeatureChange:", error)
            }
        } else {
            console.log("No onFeatureChange handler found")
            //@ts-ignore
            if (!isBatched && newVals && feature?.affectsPrice) {
                await calculatePrice(newVals)
            }
        }
    }

    const renderFeature = feature => {
        const value = values[feature.slug]
        const filteredOptions = getFilteredOptions(feature)

        if (feature.renderType && feature?.renderType?.trim() !== "") {
            const SpecialInputComponent = SpecialInputs[feature?.renderType]
            if (SpecialInputComponent)
                return (
                    <SpecialInputComponent
                        key={feature.slug}
                        feature={feature}
                        label={feature.name}
                        value={value || {}}
                        onChange={handleOnChange}
                        values={values}
                    />
                )
        }

        if (feature?.structure === "nested")
            return feature?.features?.map(field => {
                const nestedValue = value?.[field.slug]
                return (
                    <BasicInput
                        key={field.slug}
                        feature={field}
                        label={field?.name}
                        type={field.type}
                        value={
                            nestedValue !== undefined ? nestedValue : field.default || ""
                        }
                        onChange={handleOnChange}
                        options={field?.options || []}
                        className="w-100"
                    />
                )
            })

        return (
            <BasicInput
                key={feature.slug}
                feature={feature}
                label={feature.name}
                type={feature.type}
                value={value !== undefined ? value : feature.default || ""}
                onChange={handleOnChange}
                options={filteredOptions}
                required={feature.required}
                info={feature.description}
            />
        )
    }

    const getVisibleGroups = useCallback(() => {
        return featureGroups
            .map(group => ({
                ...group,
                features: getVisibleFeatures(group.features)
            }))
            .filter(group => group.features.length > 0)
    }, [featureGroups, getVisibleFeatures])

    const visibleGroups = getVisibleGroups()

    const [activeKeys, setActiveKeys] = useState(() => {
        if (isEditMode && visibleGroups.length > 0) {
            return visibleGroups.map(group => group.name)
        }
        return []
    })

    return (
        <div className={styles.configurator}>
            <Accordion
                activeKey={activeKeys}
                //@ts-ignore
                onSelect={eventKey => {
                    if (eventKey) {
                        setActiveKeys(Array.isArray(eventKey) ? eventKey : [eventKey])
                    } else {
                        setActiveKeys([])
                    }
                }}
                alwaysOpen
                className={`${styles.accordion}`}
            >
                {visibleGroups.map((group, idx) => (
                    <AccordionItem
                        key={idx}
                        eventKey={group.name}
                        className={`my-3 p-4 ${styles.group}`}
                    >
                        <AccordionHeader
                            className={`${activeKeys.includes(group.name)
                                ? styles.accordionHeaderActive
                                : null
                                } ${styles.accordionHeader}`}
                        >
                            {activeKeys.includes(group.name) ? (
                                <IoIosArrowUp size={22} />
                            ) : (
                                <IoIosArrowDown size={22} />
                            )}
                            &nbsp;<h6 className={styles.groupTitle}>{group.name}</h6>
                        </AccordionHeader>
                        <AccordionBody className={styles.accordionBody}>
                            <div className={styles.groupContent}>
                                {group.features.map(feature => renderFeature(feature))}
                            </div>
                        </AccordionBody>
                    </AccordionItem>
                ))}
            </Accordion>
        </div>
    )
}

export default Configurator
